import { authBypassed, isBackendEnabled as backendConfigured } from '../config/env';
import { sendEmailCode, verifyEmailCode } from './auth-api';
import { fetchPlayerIdentity, isBackendEnabled as backendOn } from '../net/backend';
import { getBaseUrl } from '../net/api-base';
import { workerFetch } from '../net/network-client';
import { tryOAuthSessionFromUrl, tryOAuthSessionFromDeepLink } from './oauth-exchange';
import { startOAuthLogin } from './gaupa/login';
import { onOAuthDeepLink } from './native-oauth';
import { isTelegramMiniApp } from './telegram-auth';
import {
  applyBackendLogin,
  clearSession,
  hasSession,
  getLastBackendLogin,
  getSessionToken,
  getStoredBackendLogin,
  type BackendLogin,
} from '../net/session';

export {
  applyBackendLogin,
  clearSession,
  getGaupaUserId,
  getLastBackendLogin,
  getPlayerId,
  getSessionToken,
  getStoredBackendLogin,
  getUserProfile,
  hasSession,
  saveSession,
} from '../net/session';

let authResolve: (() => void) | null = null;
let authPromise: Promise<void> | null = null;
let authGateInitialized = false;
let bootstrapDone = false;
let emailSessionId: string | null = null;

const WELCOME_BACK_MIN_MS = 2000;
let welcomeBackShownAt = 0;
let welcomeBackProfilePromise: Promise<void> | null = null;

function markWelcomeBackShown() {
  if (!welcomeBackShownAt) welcomeBackShownAt = performance.now();
}

function waitForWelcomeBackMinimum() {
  if (!welcomeBackShownAt) return Promise.resolve();
  const remaining = WELCOME_BACK_MIN_MS - (performance.now() - welcomeBackShownAt);
  if (remaining <= 0) return Promise.resolve();
  return new Promise<void>((resolve) => setTimeout(resolve, remaining));
}

export async function waitForWelcomeBackReady() {
  if (!welcomeBackShownAt) return;
  await Promise.all([waitForWelcomeBackMinimum(), welcomeBackProfilePromise ?? Promise.resolve()]);
}

function createAuthPromise() {
  if (!authPromise) {
    authPromise = new Promise<void>((resolve) => {
      authResolve = resolve;
    });
  }
  return authPromise;
}

function resolveAuth() {
  if (authResolve) {
    authResolve();
    authResolve = null;
  }
}

export function resolveAuthForDev() {
  resolveAuth();
}

export function resolveAuthIfLoggedIn() {
  if (hasSession()) resolveAuth();
}

export function showAuthStatus(message: string) {
  const el = document.getElementById('auth-status');
  if (!el) return;
  el.textContent = message;
  el.classList.remove('hidden');
}

function resetLoginUi() {
  document.querySelector('#finalLoginMenu .login-options-container')?.classList.remove('hidden');
  document.getElementById('auth-user-info')?.classList.add('hidden');
  setCodeStepVisible(false);
  emailSessionId = null;
}

/** Logo + status only (no email/OAuth rows) — used during OAuth redirect handoff. */
export function presentAuthLoading(message = 'Completing sign-in…') {
  const menu = document.getElementById('finalLoginMenu');
  menu?.classList.remove('hidden');
  hydrateLoginMenuImages();
  document.querySelector('#finalLoginMenu .login-options-container')?.classList.add('hidden');
  document.getElementById('auth-user-info')?.classList.add('hidden');
  showAuthStatus(message);
}

/** Reveal stone login rows after a failed OAuth return. */
export function presentLoginForm(message?: string) {
  const menu = document.getElementById('finalLoginMenu');
  menu?.classList.remove('hidden');
  hydrateLoginMenuImages();
  resetLoginUi();
  if (message) showAuthStatus(message);
  else document.getElementById('auth-status')?.classList.add('hidden');
}

export function showLoggedInUser(login?: BackendLogin | null) {
  const backendLogin = login ?? getLastBackendLogin() ?? getStoredBackendLogin();
  if (!backendLogin) return;

  const { displayName, isNewUser } = backendLogin;
  if (!isNewUser) markWelcomeBackShown();

  document.querySelector('#finalLoginMenu .login-options-container')?.classList.add('hidden');

  const welcomeEl = document.getElementById('auth-welcome');
  if (welcomeEl) {
    if (displayName) {
      welcomeEl.textContent = isNewUser ? `Welcome, ${displayName}!` : `Welcome back, ${displayName}!`;
    } else {
      welcomeEl.textContent = isNewUser ? 'Welcome to Booga Smash!' : 'Welcome back!';
    }
  }

  document.getElementById('auth-user-info')?.classList.remove('hidden');
  showAuthStatus('Signed in — loading your adventure…');
}

async function refreshAndShowLoggedInUser(login?: BackendLogin | null) {
  let backendLogin = login ?? getLastBackendLogin() ?? getStoredBackendLogin();
  if (!backendLogin) return;

  const menu = document.getElementById('finalLoginMenu');
  menu?.classList.remove('hidden');
  hydrateLoginMenuImages();

  document.querySelector('#finalLoginMenu .login-options-container')?.classList.add('hidden');
  showLoggedInUser(backendLogin);

  if (backendOn() && getSessionToken()) {
    try {
      const identity = await fetchPlayerIdentity();
      applyBackendLogin({
        ...backendLogin,
        ...identity,
        playerId: identity.playerId ?? undefined,
      });
      backendLogin = getLastBackendLogin() ?? {
        ...backendLogin,
        ...identity,
        playerId: identity.playerId ?? undefined,
      };
      showLoggedInUser(backendLogin);
    } catch (err) {
      console.warn('[auth] GET /player for display name failed:', err);
    }
  }
}

function hydrateLoginMenuImages() {
  const root = document.getElementById('finalLoginMenu');
  if (!root) return;
  root.querySelectorAll('img[data-src]').forEach((img) => {
    const el = img as HTMLImageElement;
    if (!el.getAttribute('src')) {
      el.src = el.dataset.src ?? '';
    }
  });
}

export function presentLoggedInWelcome(login?: BackendLogin | null) {
  if (!hasSession()) return;
  welcomeBackProfilePromise = refreshAndShowLoggedInUser(login);
}

function setCodeStepVisible(visible: boolean) {
  const row = document.getElementById('auth-code-row');
  row?.classList.toggle('hidden', !visible);
}

export function showAuthShell(opts?: { showLogin?: boolean }) {
  const shell = document.getElementById('auth-shell');
  const menu = document.getElementById('finalLoginMenu');
  shell?.classList.remove('auth-shell--hidden');
  shell?.classList.add('auth-shell--visible');
  if (opts?.showLogin !== false) {
    menu?.classList.remove('hidden');
    hydrateLoginMenuImages();
  }
}

export function hideAuthShell() {
  const shell = document.getElementById('auth-shell');
  shell?.classList.add('auth-shell--hidden');
  shell?.classList.remove('auth-shell--visible');
}

async function handleEmailSubmit() {
  const input = document.getElementById('auth-email') as HTMLInputElement | null;
  const email = input?.value?.trim() ?? '';
  if (!email) {
    showAuthStatus('Enter your email to continue.');
    return;
  }
  if (email.includes('+')) {
    showAuthStatus('Plus-addressing is not supported for email sign-in.');
    return;
  }

  showAuthStatus('Sending verification code…');

  try {
    const result = await sendEmailCode(email);
    emailSessionId = result.sessionId;
    setCodeStepVisible(true);
    document.getElementById('auth-code')?.focus();
    showAuthStatus(result.message || 'Check your email for a verification code.');
  } catch (err) {
    showAuthStatus(err instanceof Error ? err.message : 'Could not send verification code.');
  }
}

async function handleCodeSubmit() {
  const code = (document.getElementById('auth-code') as HTMLInputElement | null)?.value?.trim() ?? '';
  if (!emailSessionId) {
    showAuthStatus('Enter your email first.');
    return;
  }
  if (!/^\d{6}$/.test(code)) {
    showAuthStatus('Enter the 6-digit code from your email.');
    return;
  }

  showAuthStatus('Verifying code…');

  try {
    const login = (await verifyEmailCode(emailSessionId, code)) as BackendLogin;
    resolveAuth();
    welcomeBackProfilePromise = refreshAndShowLoggedInUser(login);
  } catch (err) {
    showAuthStatus(err instanceof Error ? err.message : 'Invalid verification code.');
  }
}

export function initAuthGate() {
  if (authGateInitialized) return;
  authGateInitialized = true;
  createAuthPromise();

  onOAuthDeepLink((url) => {
    void (async () => {
      try {
        showAuthStatus('Completing sign-in…');
        await tryOAuthSessionFromDeepLink(url);
        if (hasSession()) {
          resolveAuth();
          welcomeBackProfilePromise = refreshAndShowLoggedInUser();
        }
      } catch (err) {
        showAuthStatus(err instanceof Error ? err.message : 'OAuth sign-in failed.');
      }
    })();
  });

  document.getElementById('auth-email-submit')?.addEventListener('click', (e) => {
    e.stopPropagation();
    void handleEmailSubmit();
  });

  document.getElementById('auth-email')?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void handleEmailSubmit();
    }
  });

  document.getElementById('auth-code-submit')?.addEventListener('click', (e) => {
    e.stopPropagation();
    void handleCodeSubmit();
  });

  document.getElementById('auth-code')?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void handleCodeSubmit();
    }
  });

  document.getElementById('auth-google')?.addEventListener('click', () => {
    void startOAuthLogin('google').then((err) => {
      if (err) showAuthStatus(err);
    });
  });

  document.getElementById('auth-discord')?.addEventListener('click', () => {
    void startOAuthLogin('discord').then((err) => {
      if (err) showAuthStatus(err);
    });
  });

  window.addEventListener('booga:session-expired', () => {
    showAuthShell({ showLogin: false });
    presentLoginForm('Session expired — please sign in again.');
    authPromise = null;
    createAuthPromise();
    window.dispatchEvent(new CustomEvent('booga:restart-auth'));
  });
}

/**
 * Quietly verify a stored app session against GET /player.
 * On 401, clears the token without firing session-expired (splash still owns the UI).
 */
export async function validateExistingSession(): Promise<boolean> {
  if (authBypassed()) return true;
  if (!backendConfigured()) return false;

  const token = getSessionToken();
  if (!token) return false;

  try {
    const url = `${getBaseUrl()}/player`;
    const res = await workerFetch(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (res.status === 401) {
      clearSession();
      return false;
    }

    if (!res.ok) return false;

    const data = (await res.json().catch(() => null)) as Record<string, unknown> | null;
    if (data) {
      applyBackendLogin({
        ...getStoredBackendLogin(),
        token,
        displayName: (data.displayName ?? data.DisplayName) as string | null | undefined,
        email: (data.email ?? data.Email) as string | null | undefined,
        playerId: (data.playerId ?? data.PlayerId) as number | string | null | undefined,
        isNewUser: false,
      });
    }
    return true;
  } catch (err) {
    console.warn('[auth] Session validation failed:', err);
    return false;
  }
}

export async function ensureAuthBootstrap() {
  if (bootstrapDone) return hasSession();
  bootstrapDone = true;

  if (authBypassed()) {
    return false;
  }

  // Telegram mini apps authenticate via initData, not OAuth ?token= redirects.
  if (isTelegramMiniApp()) {
    return hasSession();
  }

  try {
    await tryOAuthSessionFromUrl();
  } catch (err) {
    console.warn('[auth] OAuth session exchange failed:', err);
  }

  return hasSession();
}

export function onLoginMenuShown() {
  if (authBypassed()) {
    resolveAuthForDev();
    return;
  }

  if (hasSession()) {
    resolveAuth();
    welcomeBackProfilePromise = refreshAndShowLoggedInUser();
  }
}

export function waitForAuth() {
  if (authBypassed() || hasSession()) {
    return Promise.resolve();
  }
  return createAuthPromise();
}

export function isAuthRequired() {
  return !authBypassed();
}

export function isLoggedIn() {
  return hasSession();
}
